import PropTypes from 'prop-types';
import cn from 'classnames';
import { memo, useState, useEffect } from 'react';
import TableCell from '@mui/material/TableCell';
import { Form } from 'react-bootstrap';
import { updateLunchOffered, createLunchRecord } from '../../utils/API/lunches';

const arePropsEqual = (prevProps, nextProps) =>
  JSON.stringify(nextProps) === JSON.stringify(prevProps);

export const LunchDayCell = memo(
  ({
    data,
    date,
    isReadOnly,
    isWeekEnd,
    isHoliday,
    isToday,
    onLunchUpdate,
  }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [isChecked, setIsChecked] = useState(Boolean(data?.offered));
    const [localData, setLocalData] = useState(data);

    // Update local state when data changes
    useEffect(() => {
      setIsChecked(Boolean(data?.offered));
      setLocalData(data);
    }, [data]);

    const cnClasses = cn({
      weekend: isWeekEnd && !isHoliday,
      holiday: isHoliday,
      today: isToday,
    });

    const handleCheckboxChange = async (event) => {
      if (isReadOnly || isLoading) return;

      const newValue = event.target.checked;
      const originalValue = isChecked;

      // Optimistically update the UI
      setIsChecked(newValue);
      setIsLoading(true);

      try {
        let updatedLunchData;

        if (localData?.id) {
          updatedLunchData = await updateLunchOffered(localData.id, newValue);
        } else if (!localData?.id && newValue) {
          // Create new lunch record if not existing
          updatedLunchData = await createLunchRecord(date, newValue);
        }

        // Update parent to update total counter
        if (updatedLunchData && onLunchUpdate) {
          onLunchUpdate(date, updatedLunchData);
        }
      } catch (error) {
        // Revert to original value on error
        setIsChecked(originalValue);
        // eslint-disable-next-line no-console
        console.error('Failed to update lunch status:', error);
      } finally {
        setIsLoading(false);
      }
    };

    return (
      <TableCell className={cnClasses}>
        {!isWeekEnd && !isHoliday ? (
          <div
            className="form-group"
            style={{
              padding: 0,
              margin: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Form.Check
              className="form-check"
              type="checkbox"
              checked={isChecked}
              onChange={handleCheckboxChange}
              disabled={isReadOnly || isLoading}
            />
          </div>
        ) : null}
      </TableCell>
    );
  },
  arePropsEqual
);

LunchDayCell.defaultProps = {
  data: null,
  date: '',
  isReadOnly: false,
  isWeekEnd: false,
  isHoliday: false,
  isToday: false,
  onLunchUpdate: () => {},
};

LunchDayCell.propTypes = {
  data: PropTypes.shape({
    id: PropTypes.number,
    date: PropTypes.string,
    offered: PropTypes.bool,
    user: PropTypes.number,
    created_by: PropTypes.string,
    created_at: PropTypes.string,
    updated_at: PropTypes.string,
  }),
  date: PropTypes.string,
  isReadOnly: PropTypes.bool,
  isWeekEnd: PropTypes.bool,
  isHoliday: PropTypes.bool,
  isToday: PropTypes.bool,
  onLunchUpdate: PropTypes.func,
};

LunchDayCell.displayName = 'LunchDayCell';
